"use client";

import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { CalendarDaysIcon, AtSymbolIcon, PhoneIcon, CreditCardIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import usePageTitle from './utils/usePageTitle';

export default function Archive() {
  usePageTitle('Archive');

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [aliases, setAliases] = useState({ email: [], phone: [] });
  const [archiveData, setArchiveData] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadArchiveData(currentUser.uid);
      } else {
        // Redirect to login if not authenticated
        window.location.href = '/';
      }
    });

    return () => unsubscribe();
  }, []);

  const loadArchiveData = async (uid) => {
    try {
      // Load subscription data
      const subscriptionRef = doc(db, 'userSubscriptions', uid);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        setSubscription(subscriptionData);
        
        // Calculate next renewal date (1 year from archive activation)
        if (subscriptionData.archivedAt) {
          const archivedDate = new Date(subscriptionData.archivedAt);
          const renewalDate = new Date(archivedDate);
          renewalDate.setFullYear(renewalDate.getFullYear() + 1);
          
          setArchiveData({
            archivedAt: subscriptionData.archivedAt,
            renewalDate: renewalDate.toISOString(),
            lastTransactionAmount: '$23.88', // $1.99 x 12 months
            lastTransactionDate: subscriptionData.archivedAt
          });
        }
      }

      // Load preserved aliases
      const userAliasesRef = doc(db, 'userAliases', uid);
      const userAliasesDoc = await getDoc(userAliasesRef);
      
      const preservedAliases = { email: [], phone: [] };
      
      if (userAliasesDoc.exists()) {
        const userData = userAliasesDoc.data();
        
        // Get email aliases
        if (userData.aliases && Array.isArray(userData.aliases)) {
          preservedAliases.email = userData.aliases.map(alias => ({
            address: alias.address || alias, // Handle both object and string formats
            domain: (alias.address || alias).includes('@') ? (alias.address || alias).split('@')[1] : 'relay.domain',
            status: 'preserved'
          }));
        }
      }

      // Load phone aliases from userAliases subcollection (primary config source)
      const userAliasesPhoneRef = collection(db, 'userAliases', uid, 'phoneAliases');
      const userAliasesPhoneDocs = await getDocs(userAliasesPhoneRef);
      
      userAliasesPhoneDocs.forEach(doc => {
        const phoneData = doc.data();
        const phoneNumber = doc.id; // Document ID is the phone number
        preservedAliases.phone.push({
          number: phoneNumber,
          label: phoneData.label || 'Phone Alias',
          status: 'preserved'
        });
      });

      // If no phone aliases in userAliases, try userMessaging as fallback
      if (preservedAliases.phone.length === 0) {
        const userMessagingRef = doc(db, 'userMessaging', uid);
        const phoneAliasesRef = collection(userMessagingRef, 'phoneAliases');
        const phoneAliasesDocs = await getDocs(phoneAliasesRef);
        
        phoneAliasesDocs.forEach(doc => {
          const phoneData = doc.data();
          const phoneNumber = doc.id; // Document ID is the phone number
          preservedAliases.phone.push({
            number: phoneNumber,
            label: 'Phone Alias',
            status: 'preserved'
          });
        });
      }

      setAliases(preservedAliases);
      setLoading(false);
    } catch (error) {
      console.error('Error loading archive data:', error);
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center text-white"
        style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
      >
        <div className="text-center">
          <img
            alt="Relay"
            src="/images/logowhite.png"
            className="h-12 w-auto mx-auto mb-8"
          />
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen text-white px-4 py-8"
      style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <img
            alt="Relay"
            src="/images/logowhite.png"
            className="h-16 w-auto mx-auto mb-8"
          />
          <h1 className="text-4xl font-light text-white mb-4">
            Your Account is Archived
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Your aliases are safely preserved for one year. All services are suspended during this time.
          </p>
        </div>

        {/* Archive Status Card */}
        <div 
          className="mb-8 rounded-2xl border border-white/10 p-8 backdrop-blur-2xl"
          style={{
            background: 'rgba(0, 0, 0, 0.3)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
          }}
        >
          <div className="flex items-center gap-x-4 mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-xl">
              <CalendarDaysIcon className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-light text-white">Archive Plan</h2>
              <p className="text-white/60">$1.99/month for data preservation</p>
            </div>
          </div>

          {archiveData && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-white/60 mb-2">Archived On</p>
                <p className="text-lg text-white">{formatDate(archiveData.archivedAt)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-white/60 mb-2">Next Renewal</p>
                <p className="text-lg text-white">{formatDate(archiveData.renewalDate)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Preserved Aliases */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Email Aliases */}
          <div 
            className="rounded-2xl border border-white/10 p-6 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center gap-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-green-500/20 rounded-lg">
                <AtSymbolIcon className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white">Email Aliases</h3>
                <p className="text-sm text-white/60">Preserved for 1 year</p>
              </div>
            </div>

            <div className="space-y-3">
              {aliases.email.length > 0 ? (
                aliases.email.map((alias, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5">
                    <span className="text-white font-mono text-sm">{alias.address}</span>
                    <span className="text-xs text-green-400 font-medium">PRESERVED</span>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-sm py-4">No email aliases found</p>
              )}
            </div>
          </div>

          {/* Phone Aliases */}
          <div 
            className="rounded-2xl border border-white/10 p-6 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center gap-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-lg">
                <PhoneIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white">Phone Numbers</h3>
                <p className="text-sm text-white/60">Preserved for 1 year</p>
              </div>
            </div>

            <div className="space-y-3">
              {aliases.phone.length > 0 ? (
                aliases.phone.map((phone, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5">
                    <div>
                      <span className="text-white font-mono text-sm">{phone.number}</span>
                      <p className="text-xs text-white/60">{phone.label}</p>
                    </div>
                    <span className="text-xs text-blue-400 font-medium">PRESERVED</span>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-sm py-4">No phone numbers found</p>
              )}
            </div>
          </div>
        </div>

        {/* Transaction Information */}
        {archiveData && (
          <div 
            className="mb-8 rounded-2xl border border-white/10 p-6 backdrop-blur-2xl"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="flex items-center gap-x-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-500/20 rounded-lg">
                <CreditCardIcon className="h-5 w-5 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-white">Last Transaction</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-white/60 mb-1">Amount</p>
                <p className="text-lg text-white">{archiveData.lastTransactionAmount}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-white/60 mb-1">Date</p>
                <p className="text-lg text-white">{formatDate(archiveData.lastTransactionDate)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Support Contact */}
        <div 
          className="mb-8 rounded-2xl border border-amber-500/20 p-6 backdrop-blur-2xl"
          style={{
            background: 'rgba(255, 191, 0, 0.05)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
          }}
        >
          <div className="flex items-start gap-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-amber-500/20 rounded-lg">
              <ExclamationCircleIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Need to Reactivate?</h3>
              <p className="text-white/80 text-sm mb-4">
                Contact our support team to reinstate your account and restore full access to all Relay services.
              </p>
              <a 
                href="mailto:support@relay.chatodyssey.com" 
                className="inline-flex items-center px-4 py-2 bg-amber-600/80 backdrop-blur-sm text-white rounded-xl hover:bg-amber-600 transition-all duration-200 border border-amber-500/30 text-sm font-medium"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>

        {/* Sign Out Button */}
        <div className="text-center">
          <button
            onClick={handleSignOut}
            className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all duration-200 border border-white/20 font-medium"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
} 